import React from 'react';
import { 
  CheckCircleIcon, 
  ExclamationTriangleIcon, 
  ClockIcon,
  ArrowPathIcon,
  CloudIcon
} from '@heroicons/react/24/outline';
import { Button } from '@/components';
import type { AutoSaveState } from '../../../hooks/useAutoSave';

interface AutoSaveIndicatorProps {
  state: AutoSaveState;
  onRetry?: () => void;
  onManualSave?: () => void;
  className?: string;
  compact?: boolean;
}

const STATUS_CONFIG = {
  idle: {
    icon: ClockIcon,
    color: 'text-gray-500',
    bgColor: 'bg-gray-50',
    borderColor: 'border-gray-200',
    message: 'Aguardando alterações...'
  },
  saving: {
    icon: ArrowPathIcon,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    message: 'Salvando...'
  },
  saved: {
    icon: CheckCircleIcon,
    color: 'text-green-600',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    message: 'Salvo automaticamente'
  },
  error: {
    icon: ExclamationTriangleIcon,
    color: 'text-red-600',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    message: 'Erro ao salvar'
  },
  offline: {
    icon: CloudIcon,
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-200',
    message: 'Offline - salvará quando conectar'
  }
};

export default function AutoSaveIndicator({
  state,
  onRetry,
  onManualSave,
  className = '',
  compact = false
}: AutoSaveIndicatorProps) {
  const config = STATUS_CONFIG[state.status];
  const Icon = config.icon;

  if (compact) {
    return (
      <div className={`inline-flex items-center gap-2 ${className}`}>
        <div className="flex items-center gap-1">
          {state.status === 'saving' ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
          ) : (
            <Icon className={`w-4 h-4 ${config.color}`} />
          )}
          <span className={`text-xs ${config.color} font-medium`}>
            {state.status === 'saved' && state.lastSaved
              ? `Salvo ${formatTimeAgo(state.lastSaved)}`
              : config.message
            }
          </span>
        </div>

        {state.hasUnsavedChanges && state.status !== 'saving' && (
          <span className="text-xs text-orange-600 font-medium">
            • Alterações não salvas
          </span>
        )}

        {state.status === 'error' && onRetry && (
          <Button
            size="sm"
            variant="outline"
            onClick={onRetry}
            className="text-xs h-6 px-2"
          >
            Tentar novamente
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className={`rounded-lg border p-3 ${config.bgColor} ${config.borderColor} ${className}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0">
            {state.status === 'saving' ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-current opacity-70"></div>
            ) : (
              <Icon className={`w-5 h-5 ${config.color}`} />
            )}
          </div>

          <div className="min-w-0">
            <div className={`text-sm font-medium ${config.color}`}>
              {config.message}
            </div>
            
            {state.lastSaved && state.status === 'saved' && (
              <div className="text-xs text-gray-500 mt-1">
                {formatDateTime(state.lastSaved)}
              </div>
            )}
            
            {state.error && state.status === 'error' && (
              <div className="text-xs text-red-600 mt-1">
                {state.error}
              </div>
            )}
            
            {state.hasUnsavedChanges && state.status !== 'saving' && (
              <div className="text-xs text-orange-600 mt-1">
                Há alterações não salvas
              </div>
            )}
          </div>
        </div>

        {/* Ações */}
        <div className="flex gap-2">
          {state.status === 'error' && onRetry && (
            <Button
              size="sm"
              variant="outline"
              onClick={onRetry}
              className={`text-xs border-red-300 text-red-700 hover:bg-red-100`}
            >
              Tentar novamente
            </Button>
          )}

          {(state.hasUnsavedChanges || state.status === 'error') && onManualSave && (
            <Button
              size="sm"
              onClick={onManualSave}
              className="text-xs"
              disabled={state.status === 'saving'}
            >
              {state.status === 'saving' ? 'Salvando...' : 'Salvar agora'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function formatTimeAgo(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);

  if (diffSeconds < 10) return 'agora';
  if (diffSeconds < 60) return `há ${diffSeconds}s`;
  if (diffMinutes === 1) return 'há 1 min';
  if (diffMinutes < 60) return `há ${diffMinutes} min`;
  
  return date.toLocaleTimeString('pt-BR', { 
    hour: '2-digit', 
    minute: '2-digit' 
  });
}

function formatDateTime(date: Date): string {
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
}